/*
  Simple symbols you can add to the start of a raw input word
  to adjust the prosody of the the resulting IPA.
*/

import { NoteClass, NoteTone, PunctuationClass, WordUnit, isPunctuation } from "./wordUnit";

const pmdToNote: Record<string, NoteClass> = {
  /** add extra stress to the word */
  '*': NoteClass.AddStress,
  /** lower primary-stress to secondary-stress in the word */
  '%': NoteClass.LowerStress,
  /** lengthen vowels */
  '+': NoteClass.LengthenVowels,
  /** pass through as-is, without additional interpretation or changes */
  '/': NoteClass.AsIs,
  /** tones */
  '1|': NoteClass.Tone,
  '2|': NoteClass.Tone,
  '3|': NoteClass.Tone,
  '4|': NoteClass.Tone,
  '5|': NoteClass.Tone,
  '/|': NoteClass.Tone,
  '\\|': NoteClass.Tone,
  '^|': NoteClass.Tone,
  '~|': NoteClass.Tone,
}

/** symbol used before pmd that's scoped beyond a single word */
const pmdScoped = '\\';
/** inside a remove-stress scope, this char tells us to leave the stress alone */
const pmdStressAsIs = "'";

/** sentence rises at the end, aka uptalk */
export const pmdUptalk = '\\/|';  // pmdScoped + rising tone /|

/** parse optional prosody markdown & add notes to the associated word */
export function parsePMD(word: string): WordUnit {
  if (word.length > 2) {
    const firstTwo = word.substring(0, 2);
    const noteTwo = pmdToNote[firstTwo];
    if (noteTwo) {
      if (noteTwo !== NoteClass.Tone) {
        return { input: word.substring(2), notes: [noteTwo] };
      }
      const tone = getTone(firstTwo);
      return { input: word.substring(2), notes: [noteTwo], tones: tone };
    }
  }

  if (word.length > 1) {
    const first = word[0];
    const note = pmdToNote[first];
    if (note) {
      return { input: word.substring(1), notes: [note] };
    }
  }

  return { input: word };
}

/** figure out the series of tones represented by the symbol */
function getTone(symbols: string): NoteTone {
  switch (symbols) {
    case '1|': return 1;
    case '2|': return 2;
    case '3|': return 3;
    case '4|': return 4;
    case '5|': return 5;
    case '/|': return 15;
    case '\\|': return 51;
    case '^|': return 242;
    case '~|': return 424;
  }
  return 3;
}


/** handle PMD that impacts more than the current word */
export class ScopedPmd {
  private sentence?: NoteClass;
  private asIs?: boolean; // in a scoped pair of slashes

  /** are we currently scoped to interpret text as-is? */
  useAsIs() { return this.asIs === true; }

  /** check if this string describes PMD that applies to more than just word */
  isScopedPmd(s: string): boolean {
    if (s === pmdUptalk) {
      // special pmd that varies based on where word occurs
      this.sentence = NoteClass.Uptalk;
      return true;
    } else if (s[0] === '/' && s[1] !== '|') {
      // e.g., the slash at the start of /use inside as-is/
      this.asIs = true;
    }
    // check if it's \ followed by valid PMD
    if (s.length !== 2 || s[0] !== pmdScoped) {
      return false;
    }
    const note = pmdToNote[s[1]];
    if (note === undefined) {
      return false;
    }
    this.sentence = note;
    return true;
  }

  /** if scoping is in effect, add it to the unit representing the word */
  addScopedNotes(units: WordUnit[], raw: string): WordUnit[] {
    // if we're lower stress in the sentence, ' tells us to leave the word alone
    const units2 = this.sentence !== NoteClass.LowerStress ? units
      : units.filter(u => u.input !== pmdStressAsIs);
    const change = units.length === units2.length;

    const result = units2.map(u => {
      // if this is a stop, reset sentence scoping
      if (u.punctuation === PunctuationClass.Stop) {
        this.sentence = undefined;
      }
      if (this.asIs) {
        return { ...u, notes: [NoteClass.AsIs] };
      }

      if (this.sentence === undefined || isPunctuation(u) || !change) {
        return u; // use unit as-is
      }
      if (u.notes === undefined) {
        // unit doesn't have notes, so use scoped note
        return { ...u, notes: [this.sentence] };
      }

      // unit overrides scoped note
      if ((this.sentence === NoteClass.LowerStress && u.notes[0] === NoteClass.AddStress)
        || (this.sentence === NoteClass.AddStress && u.notes[0] === NoteClass.LowerStress)) {
        return u;
      }

      // add scoped note to unit's notes
      return { ...u, notes: u.notes.concat(this.sentence) };
    });

    if (raw.endsWith('/')) {
      this.asIs = undefined;
    }
    return result;
  }
}

/** apply prosody markdown that varies depending on where word occurs */
export function applyVariablePmd(units: WordUnit[]): WordUnit[] {
  // immediately return if no uptalk
  if (!units.some(u => u.notes && u.notes.includes(NoteClass.Uptalk))) {
    return units;
  }

  const results: WordUnit[] = [];
  const len = units.length;
  for (let i = 0; i < len; i++) {
    const u = units[i];
    const notes = u.notes;
    if (notes && notes.includes(NoteClass.Uptalk)) {
      const without = notes.filter(n => n !== NoteClass.Uptalk);
      // uptalk -> last word in sentence marked with 5|, second to last is 3|
      if (isStopOrEnd(units[i + 1])) {  // last word in sentence
        results.push({ ...u, notes: without.concat(NoteClass.Tone), tones: 5 });
      } else if (isStopOrEnd(units[i + 2])) { // second to last word
        results.push({ ...u, notes: without.concat(NoteClass.Tone), tones: 3 });
      } else {  // all other words simply remove uptalk note
        if (notes.length === 1) {
          results.push({ ...u, notes: undefined });
        } else {
          results.push({ ...u, notes: without });
        }
      }
    } else {
      results.push(u);
    }
  }
  return results;
}
function isStopOrEnd(unit: WordUnit): boolean {
  return unit === undefined || unit.punctuation === PunctuationClass.Stop;
}
